import Vue from 'vue';
import axios from 'axios';
import store from '@/store';
import moment, { lang } from 'moment-timezone';
import i18n from '@/lang/lang';
import { globalErrorHandler } from '@/components/shared/globalErrors';
import { fromLongToShort } from '@/components/shared/languageCodes';
import actions from '@/store/modules/users/actions';
import EventBus from '@/components/shared/eventbus';
import { getWizardRunId } from '@/components/modules/aiLesson/wizardRunId';

const apiURL = process.env.VUE_APP_API_URL;
const xeropanApiUrl = process.env.VUE_APP_X_API_URL;
const xeropanExchangeApiURL = process.env.VUE_APP_XEROPAN_API_URL;
const classroomPaymentKey = process.env.VUE_APP_PAYMENT_KEY;

function fetchData(endpoint, requestType, requestBody, authTokenRequired) {
  const token = store.state.authToken;
  const timeZone = moment.tz.guess();
  const learnedLanguage = store.state.settings.learnedLanguage;
  let config = '';
  let language;
  if (i18n.locale === 'dkt_HU') {
    language = 'hu';
  } else {
    language = fromLongToShort[i18n.locale];
  }
  if (authTokenRequired !== undefined && authTokenRequired === false) {
    config = {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Accept-Language': language,
        'Time-Zone': timeZone
      }
    };
  } else if (requestBody && requestBody.generatePdf) {
    config = {
      headers: {
        Accept: 'application/octet-stream',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Accept-Language': language,
        'x-api-key': token,
        'Time-Zone': timeZone
      }
    };
  } else if (requestBody !== undefined && requestBody !== null) {
    if (
      requestBody.chatBotPartner &&
      requestBody.chatBotPartner === 'xeropan_landing_page'
    ) {
      config = {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Auth-Token': store.state.teachBotToken,
          'Time-Zone': timeZone,
          platform: 'web'
        }
      };
    } else if (Object.prototype.hasOwnProperty.call(requestBody, 'hash')) {
      config = {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Accept-Language': language,
          'x-api-key': `${token}_${requestBody.hash}`,
          'Time-Zone': timeZone
        }
      };
    } else {
      config = {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Accept-Language': language,
          'x-api-key': token,
          'Time-Zone': timeZone
        }
      };
    }
  } else {
    config = {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Accept-Language': language,
        'x-api-key': token,
        'Time-Zone': timeZone
      }
    };
  }

  if (
    (learnedLanguage &&
      requestBody &&
      requestBody.chatBotPartner === undefined) ||
    (learnedLanguage && !requestBody)
  ) {
    config.headers['Accept-Content-Language'] = learnedLanguage;
  }

  config.headers.platform = 'web';

  // AI lesson wizard correlation id. Stamped here, alongside the other unconditional
  // passes, because this is the only code every one of the five header branches above
  // runs through — including the PDF/arraybuffer branch, whose /pdf call belongs to the
  // same wizard run. Sourced from a leaf module (it imports nothing but uuid) so this
  // can never close an import cycle.
  const wizardRunId = getWizardRunId();
  if (wizardRunId && String(endpoint).indexOf('/api/ai-lessons') !== -1) {
    config.headers['x-ai-wizard-run'] = wizardRunId;
  }

  let axiosConfig;

  if (requestBody && requestBody.generatePdf) {
    axiosConfig = {
      method: requestType,
      url: endpoint,
      headers: config.headers,
      data: requestBody,
      responseType: 'arraybuffer'
    };
    // return axios(pdfAxiosConfig)
    //   .then(resp => resp)
    //   .catch((err) => {
    //     handleUnauthorizedError(err);
    //     globalErrorHandler(err);
    //   });
  } else if (
    requestType.toLowerCase() === 'post' ||
    requestType.toLowerCase() === 'get' ||
    requestType.toLowerCase() === 'put' ||
    requestType.toLowerCase() === 'delete' ||
    requestType.toLowerCase() === 'patch'
  ) {
    axiosConfig = {
      method: requestType,
      url: endpoint,
      headers: config.headers
    };
    if (
      requestType.toLowerCase() === 'get' ||
      requestType.toLowerCase() === 'delete'
    ) {
      if (requestBody) {
        axiosConfig.data = requestBody;
      }
      // return axios(axiosConfig)
      //   .then(resp => resp)
      //   .catch((err) => {
      //     handleUnauthorizedError(err);
      //     globalErrorHandler(err);
      //   });
    } else if (requestType.toLowerCase() === 'post') {
      axiosConfig.data = requestBody;
      // if (Object.prototype.hasOwnProperty.call(requestBody, 'hash')) {
      // return axios(axiosConfig)
      // .then(resp => resp)
      // .catch((err) => {
      //   handleUnauthorizedError(err);
      //   globalErrorHandler(err);
      // });
      // }
      // return axios(axiosConfig)
      //   .then(response => response)
      //   .catch((err) => {
      //     handleUnauthorizedError(err);
      //     globalErrorHandler(err);
      //   });
    } else if (
      requestType.toLowerCase() === 'patch' ||
      requestType.toLowerCase() === 'put'
    ) {
      axiosConfig.data = requestBody;
      // return axios(axiosConfig)
      //   .then(response => response)
      //   .catch((err) => {
      //     handleUnauthorizedError(err);
      //     globalErrorHandler(err);
      //   });
    }
  } else if (requestType.toLowerCase() === 'file') {
    // Either a bare File/Blob, or { file, fields } when the endpoint also wants plain
    // multipart fields next to it (the AI lesson document upload sends its extract
    // params that way). Undefined/empty fields are left out rather than sent as "".
    const formData = new FormData();
    const isBlob = typeof Blob !== 'undefined' && requestBody instanceof Blob;
    const fields = (!isBlob && requestBody && requestBody.fields) || {};
    Object.keys(fields).forEach((k) => {
      if (fields[k] !== undefined && fields[k] !== null && fields[k] !== '') formData.append(k, fields[k]);
    });
    // The file part goes LAST: the backend reads the fields before the file arrives.
    formData.append('file', isBlob ? requestBody : requestBody.file);
    config.headers['Content-Type'] = 'multipart/form-data';
    axiosConfig = { method: 'post', url: endpoint, headers: config.headers };
    axiosConfig.data = formData;
    // return axios(axiosConfig)
    //   .then(response => response)
    //   .catch((err) => {
    //     handleUnauthorizedError(err);
    //     globalErrorHandler(err);
    //   });
  }
  if (axiosConfig) {
    return axios(axiosConfig)
      .then((response) => {
        if (response.headers['token-time-left']) {
          EventBus.sessionLeft(response.headers['token-time-left']);
        }
        return response;
      })
      .catch((err) => {
        globalErrorHandler(err);
        throw err;
      });
  }

  throw new Error('Unknown HTTP method');
}

function getCurrencyExchange(url) {
  return fetch(url)
    .then(response => response.json())
    .catch(
      // eslint-disable-next-line
      error => console.log(error)
    );
}

export {
  fetchData,
  apiURL,
  xeropanApiUrl,
  xeropanExchangeApiURL,
  classroomPaymentKey,
  getCurrencyExchange
};



// WEBPACK FOOTER //
// ./src/api/api_helper.js